import React, { useState, useEffect, useCallback, useRef } from "react";
import { JsonRpc } from "@proton/js";
import AdminResolve, { isSuperAdmin } from "./AdminResolve";
import Loader from "./loader/Loader";
import MarketApprovalSection, {
  type DeclineRecord,
  type PendingMarket,
} from "./MarketApprovalSection";
import AlertPopup from "./AlertPopup";
import SuperAdminEditMarketSection from "./SuperAdminEditMarketSection";
import {
  declineMarketTransaction,
  forceResolveMarketTransaction,
  setMarketLiquidityTransaction,
} from "../utils/crypto";
import { PLATFORM_TOKEN_PRECISION, TRADING_TOKEN } from "../configs/config";

interface IlliquidMarket {
  id: number;
  question: string;
  category: string;
  image_url: string;
  outcomes_count: number;
  outcomes: Array<{ outcome_id: number; name: string }>;
  expire: number;
  suggested_by: string;
  total_collateral_in: number;
  q_yes: number;
  q_no: number;
  is_refundable: boolean;
  illiquidType: "no_trading" | "one_sided";
}

interface SuperAdminPanelProps {
  session: any;
}

interface StalledProposal {
  proposal_id: number;
  market_id: number;
  proposer: string;
  created_at: number;
  voting_end_at: string;
  is_resolved: boolean;
  winning_outcome_id: number;
  decided_by_admin: boolean;
  paused: boolean;
  marketQuestion: string;
  marketCategory: string;
  outcomes: Array<{ outcome_id: number; name: string }>;
  voteCounts: Record<number, number>;
  totalVotes: number;
}

interface ResConfig {
  winning_threshold: number;
  eligible_voters_count: number;
  resolution_expiry_sec: number;
}

// ── profit_v3 types ──

interface ProfitV3Config {
  admin: string;
  stake_token_contract: string;
  stake_token_symbol: string; // "precision,NAME"
  profit_token_contract: string;
  profit_token_symbol: string;
  prediction_contract: string;
  lock_duration: number;
  min_stake_age_sec: number;
  total_staked: number;
  total_stakers: number;
  paused: boolean;
}

interface ProfitV3LastDist {
  last_distribute_at: number;
}

interface ProfitV3Staker {
  account: string;
  total_staked: number;
  stake_count: number;
  last_staked_at: number;
  last_updated: number;
}

interface ProfitV3Balance {
  user: string;
  balance: string; // asset string like "12.345678 XUSDC"
}

function parseProfitSymbol(sym: string): { precision: number; name: string } {
  if (typeof sym === "string" && sym.includes(",")) {
    const parts = sym.split(",");
    return { precision: parseInt(parts[0], 10), name: parts[1] };
  }
  return { precision: 4, name: sym || "XPRED" };
}

function formatProfitAmount(raw: number, precision: number): string {
  const divisor = Math.pow(10, precision);
  return (raw / divisor).toFixed(precision);
}

const SuperAdminPanel: React.FC<SuperAdminPanelProps> = ({ session }) => {
  const [activeTab, setActiveTab] = useState<
    | "forceresolve"
    | "editany"
    | "resolvestalled"
    | "platformprofit"
    | "marketapproval"
    | "illiquid"
  >("forceresolve");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [stalledProposals, setStalledProposals] = useState<StalledProposal[]>(
    [],
  );
  const [loadingStalled, setLoadingStalled] = useState(false);
  const [selectedStalled, setSelectedStalled] =
    useState<StalledProposal | null>(null);
  const [stalledOutcome, setStalledOutcome] = useState<number | null>(null);
  const [resolvingStalled, setResolvingStalled] = useState(false);
  const [resConfig, setResConfig] = useState<ResConfig | null>(null);

  // ── Platform Profit (profit_v3) state ──
  const [profitConfig, setProfitConfig] = useState<ProfitV3Config | null>(null);
  const [lastDist, setLastDist] = useState<ProfitV3LastDist | null>(null);
  const [profitStakers, setProfitStakers] = useState<ProfitV3Staker[]>([]);
  const [profitBalances, setProfitBalances] = useState<ProfitV3Balance[]>([]);
  const [loadingProfit, setLoadingProfit] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [countdown, setCountdown] = useState("");
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // ── Market Approval state ──
  const [pendingMarkets, setPendingMarkets] = useState<PendingMarket[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [liquidityInputs, setLiquidityInputs] = useState<
    Record<number, string>
  >({});
  const [declineInputs, setDeclineInputs] = useState<Record<number, string>>(
    {},
  );
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [decliningId, setDecliningId] = useState<number | null>(null);
  const [declineConfirmMarketId, setDeclineConfirmMarketId] = useState<
    number | null
  >(null);
  const [platformPoolBalance, setPlatformPoolBalance] = useState({
    totalDeposited: 0,
    available: 0,
  });

  // ── Illiquid Markets state ──
  const [illiquidMarkets, setIlliquidMarkets] = useState<IlliquidMarket[]>([]);
  const [loadingIlliquid, setLoadingIlliquid] = useState(false);
  const [markingRefundableId, setMarkingRefundableId] = useState<number | null>(
    null,
  );

  const currentUser = session?.auth?.actor?.toString() || "";
  const isUserSuperAdmin = isSuperAdmin(currentUser);
  const declineConfirmMarket =
    pendingMarkets.find((market) => market.id === declineConfirmMarketId) ??
    null;

  const showToast = useCallback(
    (message: string, type: "success" | "error" = "success") => {
      setToast({ message, type });
      setTimeout(() => setToast(null), 5000);
    },
    [],
  );

  const SCALE = 1_000_000;

  const computeUsdtCost = (bValue: number, outcomesCount: number): number => {
    if (bValue <= 0 || outcomesCount < 2) return 0;
    return bValue * Math.log(outcomesCount);
  };

  const fetchPendingMarkets = useCallback(async () => {
    if (!session) return;
    setLoadingPending(true);
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_RPC_ENDPOINT ||
          process.env.REACT_APP_PROTON_ENDPOINT ||
          "https://proton.greymass.com",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      // Fetch all markets
      const result = await rpc.get_table_rows({
        json: true,
        code: contractName,
        scope: contractName,
        table: "markets3",
        limit: 1000,
      });

      // Fetch all decline records
      let declineRecords: DeclineRecord[] = [];
      try {
        const declineResult = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: contractName,
          table: "mktdecline",
          limit: 1000,
        });
        declineRecords = declineResult.rows;
      } catch (e) {
        console.error("Error fetching decline records:", e);
      }

      const nowSec = Math.floor(Date.now() / 1000);

      // Filter: pending markets (not live, not resolved, not expired)
      const pending = result.rows.filter((row: any) => {
        const expireSec =
          typeof row.expire === "string"
            ? Math.floor(new Date(row.expire + "Z").getTime() / 1000)
            : row.expire || 0;
        const isExpired = expireSec > 0 && expireSec < nowSec;
        const status = Number(row.status ?? 0);
        return !row.isLive && !row.resolved && !isExpired && status !== 100;
      });

      // Fetch approval counts for each pending market
      const pendingWithData: PendingMarket[] = [];
      for (const row of pending) {
        let approvalCount = 0;
        try {
          const approvalsResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: row.id.toString(),
            table: "mktintrst",
            limit: 100,
          });
          approvalCount = approvalsResult.rows.length;
        } catch (e) {
          console.error(`Error fetching approvals for market ${row.id}:`, e);
        }

        const decline =
          declineRecords.find((d: any) => d.market_id === row.id) || null;

        const expireSec =
          typeof row.expire === "string"
            ? Math.floor(new Date(row.expire + "Z").getTime() / 1000)
            : row.expire || 0;

        // Fetch outcomes for this market
        let outcomes: Array<{ outcome_id: number; name: string }> = [];
        try {
          const outcomesResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: row.id.toString(),
            table: "outcomes",
            limit: 100,
          });
          outcomes = outcomesResult.rows.map((r: any) => ({
            outcome_id: r.outcome_id,
            name: r.name,
          }));
        } catch (e) {
          console.error(`Error fetching outcomes for market ${row.id}:`, e);
        }

        pendingWithData.push({
          id: row.id,
          question: row.question,
          category: row.category,
          image_url: row.image_url || "",
          resolved: row.resolved || false,
          suggested_by: row.suggested_by || "",
          isLive: row.isLive || false,
          status: row.status ?? 1,
          outcomes_count: row.outcomes_count || 2,
          outcomes,
          b: row.b || 0,
          approved_by: row.approved_by || "",
          expire: expireSec,
          approvalCount,
          declineRecord: decline,
        });
      }

      setPendingMarkets(pendingWithData);

      // Fetch platform pool balance
      try {
        const poolResult = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: contractName,
          table: "platformpool",
          limit: 1,
        });
        if (poolResult.rows.length > 0) {
          const divisor = Math.pow(10, PLATFORM_TOKEN_PRECISION);
          const row = poolResult.rows[0];
          setPlatformPoolBalance({
            totalDeposited: Number(row.total_deposited || 0) / divisor,
            available: Number(row.available || 0) / divisor,
          });
        } else {
          setPlatformPoolBalance({ totalDeposited: 0, available: 0 });
        }
      } catch (e) {
        console.error("Error fetching pool balance:", e);
        setPlatformPoolBalance({ totalDeposited: 0, available: 0 });
      }
    } catch (error) {
      console.error("Error fetching pending markets:", error);
    } finally {
      setLoadingPending(false);
    }
  }, [session]);

  const handleSetLiquidity = async (marketId: number) => {
    if (!session) return;
    const bInput = liquidityInputs[marketId];
    const bVal = parseFloat(bInput);
    if (!bInput || isNaN(bVal) || bVal < 10 || bVal > 1000) {
      showToast("Liquidity (b) must be between 10 and 1000", "error");
      return;
    }

    const bScaled = Math.round(parseFloat(bInput));
    
    setApprovingId(marketId);
    try {
      await setMarketLiquidityTransaction(session, {
        market_id: marketId,
        new_b: bScaled,
      });
      showToast(
        `Liquidity set for market #${marketId}. Market is now live.`,
        "success",
      );
      setPendingMarkets((current) =>
        current.filter((market) => market.id !== marketId),
      );
      setLiquidityInputs((current) => {
        const next = { ...current };
        delete next[marketId];
        return next;
      });
      window.setTimeout(() => {
        fetchPendingMarkets();
      }, 1500);
    } catch (error: any) {
      console.error("Error setting liquidity:", error);
      showToast(
        "Failed to set liquidity: " + (error?.message || error),
        "error",
      );
    } finally {
      setApprovingId(null);
    }
  };

  const handleDeclineMarket = useCallback((marketId: number) => {
    const comment = declineInputs[marketId];
    if (!comment || !comment.trim()) {
      showToast("Please enter a reason for declining", "error");
      return;
    }
    if (comment.length > 500) {
      showToast("Decline comment too long (max 500 chars)", "error");
      return;
    }

    setDeclineConfirmMarketId(marketId);
  }, [declineInputs, showToast]);

  const confirmDeclineMarket = useCallback(async () => {
    if (!session || declineConfirmMarketId === null) return;

    const marketId = declineConfirmMarketId;
    const comment = declineInputs[marketId];
    if (!comment || !comment.trim()) {
      showToast("Please enter a reason for declining", "error");
      setDeclineConfirmMarketId(null);
      return;
    }
    if (comment.length > 500) {
      showToast("Decline comment too long (max 500 chars)", "error");
      setDeclineConfirmMarketId(null);
      return;
    }

    setDeclineConfirmMarketId(null);
    setDecliningId(marketId);
    try {
      await declineMarketTransaction(session, {
        admin: session.auth.actor.toString(),
        market_id: marketId,
        comment,
      });
      showToast(`Market #${marketId} declined.`, "success");
      setDeclineInputs((prev) => {
        const next = { ...prev };
        delete next[marketId];
        return next;
      });
      fetchPendingMarkets();
    } catch (error: any) {
      console.error("Error declining market:", error);
      showToast(
        "Failed to decline market: " + (error?.message || error),
        "error",
      );
    } finally {
      setDecliningId(null);
    }
  }, [
    declineConfirmMarketId,
    declineInputs,
    fetchPendingMarkets,
    session,
    showToast,
  ]);

  const fetchStalledProposals = useCallback(async () => {
    if (!session) return;
    setLoadingStalled(true);
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_RPC_ENDPOINT ||
          process.env.REACT_APP_PROTON_ENDPOINT ||
          "https://proton.greymass.com",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const configResult = await rpc.get_table_rows({
        json: true,
        code: contractName,
        scope: contractName,
        table: "resconfig",
        limit: 1,
      });
      const fetchedConfig: ResConfig | null =
        configResult.rows.length > 0 ? configResult.rows[0] : null;
      setResConfig(fetchedConfig);
      const threshold = fetchedConfig?.winning_threshold || 5;

      const proposalsResult = await rpc.get_table_rows({
        json: true,
        code: contractName,
        scope: contractName,
        table: "proposals",
        limit: 200,
      });

      const now = Math.floor(Date.now() / 1000);
      const stalled: StalledProposal[] = [];

      for (const prop of proposalsResult.rows) {
        if (prop.is_resolved) continue;
        const votingEndSec = Math.floor(
          new Date(prop.voting_end_at + "Z").getTime() / 1000,
        );
        if (now < votingEndSec) continue;

        let marketQuestion = "Market #" + prop.market_id;
        let marketCategory = "";
        let outcomes: Array<{ outcome_id: number; name: string }> = [];
        try {
          const marketResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: contractName,
            table: "markets3",
            lower_bound: prop.market_id,
            upper_bound: prop.market_id,
            limit: 1,
          });
          if (marketResult.rows.length > 0) {
            marketQuestion = marketResult.rows[0].question;
            marketCategory = marketResult.rows[0].category || "";
          }
          const outcomesResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: prop.market_id.toString(),
            table: "outcomes",
            limit: 100,
          });
          outcomes = outcomesResult.rows.map((r: any) => ({
            outcome_id: r.outcome_id,
            name: r.name,
          }));
        } catch (err) {
          console.error(
            "Error fetching market data for proposal",
            prop.proposal_id,
            err,
          );
        }

        const voteCounts: Record<number, number> = {};
        let totalVotes = 0;
        try {
          const votesResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: prop.proposal_id.toString(),
            table: "propvotes",
            limit: 200,
          });
          totalVotes = votesResult.rows.length;
          for (const vote of votesResult.rows) {
            const oid = vote.chosen_outcome;
            voteCounts[oid] = (voteCounts[oid] || 0) + 1;
          }
        } catch (err) {
          console.error(
            "Error fetching votes for proposal",
            prop.proposal_id,
            err,
          );
        }

        // const anyOutcomePassed = Object.values(voteCounts).some(
        //   (c) => c >= threshold,
        // );
        // if (anyOutcomePassed) continue;

        stalled.push({
          ...prop,
          marketQuestion,
          marketCategory,
          outcomes,
          voteCounts,
          totalVotes,
        });
      }

      setStalledProposals(stalled);
    } catch (error) {
      console.error("Error fetching stalled proposals:", error);
    } finally {
      setLoadingStalled(false);
    }
  }, [session]);

  const profitV3Contract =
    process.env.REACT_APP_PROFITV3_CONTRACT || "xpredadmin";

  const fetchProfitData = useCallback(async () => {
    if (!session) return;
    setLoadingProfit(true);
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_RPC_ENDPOINT ||
          process.env.REACT_APP_PROTON_ENDPOINT ||
          "https://proton.eosusa.io",
      );

      // Fetch config singleton
      const configRes = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "config",
        limit: 1,
      });
      const cfg: ProfitV3Config | null =
        configRes.rows.length > 0 ? configRes.rows[0] : null;
      setProfitConfig(cfg);

      // Fetch lastdist singleton
      const lastDistRes = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "lastdist",
        limit: 1,
      });
      setLastDist(lastDistRes.rows.length > 0 ? lastDistRes.rows[0] : null);

      // Fetch all stakers
      const stakersRes = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "stakers",
        limit: 200,
      });
      setProfitStakers(stakersRes.rows || []);

      // Fetch all profit balances
      const balancesRes = await rpc.get_table_rows({
        json: true,
        code: profitV3Contract,
        scope: profitV3Contract,
        table: "profitbal",
        limit: 200,
      });
      setProfitBalances(balancesRes.rows || []);
    } catch (error) {
      console.error("Error fetching profit_v3 data:", error);
    } finally {
      setLoadingProfit(false);
    }
  }, [session, profitV3Contract]);

  const fetchIlliquidMarkets = useCallback(async () => {
    if (!session) return;
    setLoadingIlliquid(true);
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_RPC_ENDPOINT ||
          process.env.REACT_APP_PROTON_ENDPOINT ||
          "https://proton.greymass.com",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";

      const result = await rpc.get_table_rows({
        json: true,
        code: contractName,
        scope: contractName,
        table: "markets3",
        limit: 1000,
      });

      const nowSec = Math.floor(Date.now() / 1000);
      const illiquid: IlliquidMarket[] = [];

      for (const row of result.rows) {
        const expireSec =
          typeof row.expire === "string"
            ? Math.floor(new Date(row.expire + "Z").getTime() / 1000)
            : row.expire || 0;
        const isExpired = expireSec > 0 && expireSec < nowSec;
        if (!isExpired || row.resolved) continue;

        const totalIn = row.total_collateral_in || 0;
        const qYes = row.q_yes || 0;
        const qNo = row.q_no || 0;
        const outcomesCount = row.outcomes_count || 2;

        let illiquidType: "no_trading" | "one_sided" | null = null;
        if (totalIn === 0) {
          illiquidType = "no_trading";
        } else if (outcomesCount === 2 && (qYes === 0 || qNo === 0)) {
          illiquidType = "one_sided";
        }

        if (!illiquidType) continue;

        // Fetch outcomes
        let outcomes: Array<{ outcome_id: number; name: string }> = [];
        try {
          const outcomesResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: row.id.toString(),
            table: "outcomes",
            limit: 100,
          });
          outcomes = outcomesResult.rows.map((r: any) => ({
            outcome_id: r.outcome_id,
            name: r.name,
          }));
        } catch (e) {
          console.error(`Error fetching outcomes for market ${row.id}:`, e);
        }

        illiquid.push({
          id: row.id,
          question: row.question,
          category: row.category,
          image_url: row.image_url || "",
          outcomes_count: outcomesCount,
          outcomes,
          expire: expireSec,
          suggested_by: row.suggested_by || "",
          total_collateral_in: totalIn,
          q_yes: qYes,
          q_no: qNo,
          is_refundable: row.is_refundable || false,
          illiquidType,
        });
      }

      setIlliquidMarkets(illiquid);
    } catch (error) {
      console.error("Error fetching illiquid markets:", error);
    } finally {
      setLoadingIlliquid(false);
    }
  }, [session]);

  useEffect(() => {
    if (session && activeTab === "resolvestalled") {
      fetchStalledProposals();
    }
    if (session && activeTab === "platformprofit") {
      fetchProfitData();
    }
    if (session && activeTab === "marketapproval") {
      fetchPendingMarkets();
    }
    if (session && activeTab === "illiquid") {
      fetchIlliquidMarkets();
    }
  }, [
    session,
    activeTab,
    fetchStalledProposals,
    fetchProfitData,
    fetchPendingMarkets,
    fetchIlliquidMarkets,
  ]);

  // Countdown timer for next distribution
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (activeTab !== "platformprofit" || !lastDist) {
      setCountdown("");
      return;
    }
    const COOLDOWN = 7 * 24 * 60 * 60; // 1 week
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      const nextAvailable = lastDist.last_distribute_at + COOLDOWN;
      const remaining = nextAvailable - now;
      if (remaining <= 0) {
        setCountdown("Available now");
      } else {
        const days = Math.floor(remaining / 86400);
        const hrs = Math.floor((remaining % 86400) / 3600);
        const mins = Math.floor((remaining % 3600) / 60);
        const secs = remaining % 60;
        const parts: string[] = [];
        if (days > 0) parts.push(`${days}d`);
        if (hrs > 0) parts.push(`${hrs}h`);
        if (mins > 0) parts.push(`${mins}m`);
        if (parts.length === 0 || (days === 0 && hrs === 0))
          parts.push(`${secs}s`);
        setCountdown(parts.join(" "));
      }
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [activeTab, lastDist]);

  const handleDistribute = async () => {
    if (!session) return;
    const confirmMsg =
      "Are you sure you want to distribute platform profit to all eligible stakers?";
    if (!window.confirm(confirmMsg)) return;
    setDistributing(true);
    try {
      await session.transact({
        actions: [
          {
            account: profitV3Contract,
            name: "distribute",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: {},
          },
        ],
      });
      showToast("Profit distributed successfully!", "success");
      fetchProfitData();
    } catch (error: any) {
      console.error("Error distributing:", error);
      showToast("Failed to distribute: " + (error?.message || error), "error");
    } finally {
      setDistributing(false);
    }
  };

  const handlePauseToggle = async () => {
    if (!session || !profitConfig) return;
    const newPaused = !profitConfig.paused;
    const confirmMsg = newPaused
      ? "Are you sure you want to PAUSE the profit contract? Staking, claiming, and distributing will be disabled."
      : "Are you sure you want to UNPAUSE the profit contract? All operations will be re-enabled.";
    if (!window.confirm(confirmMsg)) return;
    setTogglingPause(true);
    try {
      await session.transact({
        actions: [
          {
            account: profitV3Contract,
            name: "pause",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: { paused: newPaused },
          },
        ],
      });
      showToast(newPaused ? "Contract paused" : "Contract unpaused", "success");
      fetchProfitData();
    } catch (error: any) {
      console.error("Error toggling pause:", error);
      showToast(
        "Failed to toggle pause: " + (error?.message || error),
        "error",
      );
    } finally {
      setTogglingPause(false);
    }
  };

  const handleMarkRefundable = async (marketId: number) => {
    if (!session) return;
    if (
      !window.confirm(
        `Are you sure you want to mark market #${marketId} as refundable? This will allow traders to claim back their invested amounts.`,
      )
    )
      return;

    setMarkingRefundableId(marketId);
    try {
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";
      await session.transact({
        actions: [
          {
            account: contractName,
            name: "forceres",
            authorization: [
              {
                actor: session.auth.actor,
                permission: session.auth.permission,
              },
            ],
            data: {
              market_id: marketId,
            },
          },
        ],
      });
      showToast(`Market #${marketId} marked as refundable.`, "success");
      fetchIlliquidMarkets();
    } catch (error: any) {
      console.error("Error marking market as refundable:", error);
      showToast(
        "Failed to mark as refundable: " + (error?.message || error),
        "error",
      );
    } finally {
      setMarkingRefundableId(null);
    }
  };

  // Check if user is actually a SuperAdmin - must be after all hooks
  if (!isUserSuperAdmin) {
    return (
      <div className="admin-panel">
        <div className="admin-section">
          <h2>Access Denied</h2>
          <p>You do not have SuperAdmin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel superadmin-panel">
      <AlertPopup
        open={declineConfirmMarket != null}
        alertType="danger"
        actionText="Decline Market"
        heading={
          declineConfirmMarket
            ? `Decline market #${declineConfirmMarket.id}?`
            : "Decline market?"
        }
        message={
          declineConfirmMarket
            ? `This will decline "${declineConfirmMarket.question}" and record your comment as the rejection reason.`
            : "This will decline the selected market."
        }
        onCancel={() => setDeclineConfirmMarketId(null)}
        onProceed={confirmDeclineMarket}
        proceedLabel="Decline Market"
      />

      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.message}
          <button onClick={() => setToast(null)} className="toast-close">
            &times;
          </button>
        </div>
      )}

      <div className="admin-header">
        <h2>SuperAdmin Panel</h2>
        <p
          className="superadmin-warning"
          style={{ color: "#ff6b6b", marginTop: "8px" }}
        >
          You have elevated privileges. Use with caution.
        </p>
      </div>

      <div className="admin-tabs">
        <button
            className={activeTab === "platformprofit" ? "active" : ""}
            onClick={() => setActiveTab("platformprofit")}
            style={{
              backgroundColor:
                activeTab === "platformprofit" ? "#10b981" : undefined,
            }}
          >
          Platform Profit
        </button>
        <button
            className={activeTab === "marketapproval" ? "active" : ""}
            onClick={() => setActiveTab("marketapproval")}
            style={{
              backgroundColor:
                activeTab === "marketapproval" ? "#3b82f6" : undefined,
            }}
          >
          Market Approval
        </button>
        <button
          className={activeTab === "editany" ? "active" : ""}
          onClick={() => setActiveTab("editany")}
        >
          Edit Any Market
        </button>
        <button
          className={activeTab === "resolvestalled" ? "active" : ""}
          onClick={() => setActiveTab("resolvestalled")}
          style={{
            backgroundColor:
              activeTab === "resolvestalled" ? "#f59e0b" : undefined,
          }}
        >
          Resolve Stalled Resolution
        </button>
        <button
          className={activeTab === "illiquid" ? "active" : ""}
          onClick={() => setActiveTab("illiquid")}
          style={{
            backgroundColor: activeTab === "illiquid" ? "#f59e0b" : undefined,
          }}
        >
          Illiquid Markets
        </button>
        <button
          className={activeTab === "forceresolve" ? "active" : ""}
          onClick={() => setActiveTab("forceresolve")}
          style={{
            backgroundColor:
              activeTab === "forceresolve" ? "#dc2626" : undefined,
          }}
        >
          Force Resolve
        </button>
      </div>

      {activeTab === "forceresolve" && (
        <AdminResolve
          session={session}
          contractName={process.env.REACT_APP_CONTRACT_NAME || "xpredict"}
        />
      )}

      {activeTab === "resolvestalled" && (
        <div className="admin-section">
          <h3>Resolve Stalled Resolutions</h3>
          <p className="section-description" style={{ color: "#f59e0b" }}>
            {/* These proposals have exceeded their voting period without any
            outcome reaching the minimum vote threshold (
            {resConfig?.winning_threshold || 5} votes). Select a proposal and
            choose the winning outcome to resolve it. */}

            These proposals have completed their voting period and are ready to be resolved.
            Select a proposal and choose the winning outcome to resolve it.
          </p>

          {loadingStalled ? (
            <Loader
              label="Loading stalled proposals..."
              className="app-loader-compact"
            />
          ) : stalledProposals.length === 0 ? (
            <div className="no-markets">No stalled proposals found.</div>
          ) : (
            <div className="market-cards">
              {stalledProposals.map((prop) => {
                const now = Math.floor(Date.now() / 1000);
                const votingEndSec = Math.floor(
                  new Date(prop.voting_end_at + "Z").getTime() / 1000,
                );
                const elapsedSec = now - votingEndSec;
                const elapsedDays = Math.floor(elapsedSec / 86400);
                const elapsedHours = Math.floor((elapsedSec % 86400) / 3600);
                const elapsedMinutes = Math.floor((elapsedSec % 3600) / 60);
                let elapsedStr = "";
                if (elapsedDays > 0) elapsedStr += elapsedDays + "d ";
                if (elapsedHours > 0) elapsedStr += elapsedHours + "h ";
                elapsedStr += elapsedMinutes + "m ago";

                return (
                  <div
                    key={prop.proposal_id}
                    className={`market-card ${selectedStalled?.proposal_id === prop.proposal_id ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedStalled(prop);
                      setStalledOutcome(null);
                    }}
                    style={{
                      border:
                        selectedStalled?.proposal_id === prop.proposal_id
                          ? "2px solid #f59e0b"
                          : "1px solid #374151",
                      backgroundColor:
                        selectedStalled?.proposal_id === prop.proposal_id
                          ? "rgba(245, 158, 11, 0.1)"
                          : "transparent",
                    }}
                  >
                    <div
                      className="market-question"
                      style={{ fontWeight: "bold", marginBottom: "8px" }}
                    >
                      {prop.marketQuestion}
                    </div>
                    <div
                      className="market-meta"
                      style={{ fontSize: "0.85em", color: "#9ca3af" }}
                    >
                      Proposal #{prop.proposal_id} | Market #{prop.market_id}
                      {prop.marketCategory && " | " + prop.marketCategory}
                    </div>
                    <div
                      style={{
                        fontSize: "0.85em",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      Proposed by: {prop.proposer} | Votes: {prop.totalVotes}
                    </div>
                    <div
                      style={{
                        fontSize: "0.8em",
                        color: "#6b7280",
                        marginTop: "4px",
                      }}
                    >
                      {prop.outcomes.map((o) => (
                        <span
                          key={o.outcome_id}
                          style={{ marginRight: "12px" }}
                        >
                          {o.name}: {prop.voteCounts[o.outcome_id] || 0} votes
                        </span>
                      ))}
                    </div>
                    <div
                      style={{
                        fontSize: "0.85em",
                        color: "#f59e0b",
                        marginTop: "6px",
                      }}
                    >
                      Voting ended:{" "}
                      {new Date(prop.voting_end_at + "Z").toLocaleString()} (
                      {elapsedStr})
                    </div>
                    {selectedStalled?.proposal_id === prop.proposal_id && (
                        <div
                          className="resolution-form"
                          style={{
                            marginTop: "20px",
                            padding: "20px",
                            backgroundColor: "rgba(245, 158, 11, 0.1)",
                            borderRadius: "8px",
                            border: "1px solid #f59e0b",
                          }}
                        >
                          <h4>
                            Resolve Stalled Proposal: # {selectedStalled?.proposal_id}
                          </h4>
                          <h4>Market: #{selectedStalled.market_id}</h4>
                          <p
                            style={{
                              color: "#9ca3af",
                              fontSize: "0.9em",
                              marginBottom: "12px",
                            }}
                          >
                            Total votes : {selectedStalled.totalVotes}
                          </p>

                          <div style={{ marginBottom: "15px" }}>
                            {/* {selectedStalled.outcomes.map((o) => (
                              <div
                                key={o.outcome_id}
                                style={{
                                  fontSize: "0.9em",
                                  color: "#d1d5db",
                                  marginBottom: "4px",
                                }}
                              >
                                {o.name}: {selectedStalled.voteCounts[o.outcome_id] || 0} /{" "}
                                {selectedStalled.totalVotes} votes
                              </div>
                            ))} */}
                            <div style={{ marginBottom: "15px" }}>
                              {selectedStalled.outcomes.map((o) => {
                                const votes =
                                  selectedStalled.voteCounts[o.outcome_id] || 0;

                                const totalVotes =
                                  selectedStalled.totalVotes || 0;

                                const percentage =
                                  totalVotes > 0
                                    ? ((votes / totalVotes) * 100).toFixed(1)
                                    : 0;

                                return (
                                  <div
                                    key={o.outcome_id}
                                    style={{
                                      marginBottom: "12px",
                                      maxWidth: "30rem",
                                    }}
                                  >
                                    {/* LABEL ROW */}
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        marginBottom: "6px",
                                        fontSize: "0.9em",
                                        color: "#d1d5db",
                                      }}
                                    >
                                      <span>
                                        <b>{o.name}</b>: {votes} / {totalVotes} votes
                                      </span>
                                    </div>

                                    {/* PROGRESS BAR */}
                                    <div
                                      style={{
                                        width: "100%",
                                        height: "10px",
                                        backgroundColor: "#374151",
                                        borderRadius: "999px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${percentage}%`,
                                          height: "100%",
                                          background:
                                            stalledOutcome === o.outcome_id
                                              ? "#f59e0b"
                                              : "#10b981",
                                          transition: "width 0.3s ease",
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="form-group">
                            <label
                              style={{
                                display: "block",
                                marginBottom: "10px",
                                fontWeight: "bold",
                              }}
                            >
                              Select Winning Outcome
                            </label>
                            <div
                              className="outcomes-selection"
                              style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                            >
                              {selectedStalled.outcomes.map((outcome) => (
                                <button
                                  key={outcome.outcome_id}
                                  // onClick={() => setStalledOutcome(outcome.outcome_id)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStalledOutcome(outcome.outcome_id);
                                  }}
                                  style={{
                                    padding: "10px 20px",
                                    borderRadius: "6px",
                                    border:
                                      stalledOutcome === outcome.outcome_id
                                        ? "2px solid #f59e0b"
                                        : "1px solid #ffffff",
                                    backgroundColor:
                                      stalledOutcome === outcome.outcome_id
                                        ? "#f59e0b"
                                        : "transparent",
                                    color:
                                      stalledOutcome === outcome.outcome_id
                                        ? "#000"
                                        : "#e5e7eb",
                                    cursor: "pointer",
                                    fontWeight:
                                      stalledOutcome === outcome.outcome_id
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {outcome.name} (
                                  {selectedStalled.voteCounts[outcome.outcome_id] || 0}{" "}
                                  votes)
                                </button>
                              ))}
                            </div>
                          </div>

                          <button
                            onClick={async () => {
                              if (!selectedStalled || stalledOutcome === null) return;
                              const confirmMsg = `Are you sure you want to resolve proposal #${selectedStalled.proposal_id} for market "${selectedStalled.marketQuestion}" with outcome "${selectedStalled.outcomes.find((o) => o.outcome_id === stalledOutcome)?.name}"?\n\nThis action cannot be undone.`;
                              if (!window.confirm(confirmMsg)) return;
                              console.log('Resolving proposal with outcome_id:', stalledOutcome);
                              setResolvingStalled(true);
                              try {
                                await forceResolveMarketTransaction(session, {
                                  market_id: selectedStalled.market_id,
                                  outcome_id: stalledOutcome,
                                });
                                showToast("Proposal resolved successfully!", "success");
                                setSelectedStalled(null);
                                setStalledOutcome(null);
                                fetchStalledProposals();
                              } catch (error: any) {
                                console.error("Error resolving proposal:", error);
                                showToast(
                                  "Failed to resolve proposal: " + error.message,
                                  "error",
                                );
                              } finally {
                                setResolvingStalled(false);
                              }
                            }}
                            disabled={resolvingStalled || stalledOutcome === null}
                            style={{
                              marginTop: "20px",
                              padding: "12px 24px",
                              backgroundColor:
                                stalledOutcome === null ? "#374151" : "#f59e0b",
                              color: stalledOutcome === null ? "#9ca3af" : "#000",
                              border: "none",
                              borderRadius: "6px",
                              cursor: stalledOutcome === null ? "not-allowed" : "pointer",
                              fontWeight: "bold",
                              fontSize: "1em",
                            }}
                          >
                            {resolvingStalled ? "Resolving..." : "Resolve Stalled Proposal"}
                          </button>
                          <p               
                          style={{
                              fontSize: "0.85em",
                              color: "#aaa",
                              marginTop: "8px",
                            }}>
                            Note: This ACTION will RESOLVE the market in the selected FAVOR. Please ensure you have verified the outcome before resolving.
                          
                          </p>
                        </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === "platformprofit" && (
        <div className="admin-section sa-profit-section">
          <h3>Platform Profit Distribution</h3>
          <p className="section-description">
            Distribute accumulated platform fees to all eligible stakers
            proportionally.
          </p>

          {loadingProfit ? (
            <Loader
              label="Loading profit data..."
              className="app-loader-compact"
            />
          ) : !profitConfig ? (
            <div className="no-markets">
              profit_v3 contract is not configured yet.
            </div>
          ) : (
            <>
              {/* Last Distribution & Countdown */}
              <div className="sa-profit-info-grid">
                <div className="sa-profit-info-card">
                  <span className="sa-profit-info-label">
                    Last Distribution
                  </span>
                  <span className="sa-profit-info-value">
                    {lastDist && lastDist.last_distribute_at > 0
                      ? new Date(
                          lastDist.last_distribute_at * 1000,
                        ).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }) +
                        " at " +
                        new Date(
                          lastDist.last_distribute_at * 1000,
                        ).toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                      : "Never"}
                  </span>
                </div>
                <div className="sa-profit-info-card">
                  <span className="sa-profit-info-label">
                    Next Distribution
                  </span>
                  <span
                    className={`sa-profit-info-value ${countdown === "Available now" ? "available" : ""}`}
                  >
                    {lastDist ? countdown : "Available now"}
                  </span>
                </div>
                <div className="sa-profit-info-card">
                  <span className="sa-profit-info-label">Total Staked</span>
                  <span className="sa-profit-info-value">
                    {formatProfitAmount(
                      profitConfig.total_staked,
                      parseProfitSymbol(profitConfig.stake_token_symbol)
                        .precision,
                    )}{" "}
                    <small>
                      {parseProfitSymbol(profitConfig.stake_token_symbol).name}
                    </small>
                  </span>
                </div>
                <div className="sa-profit-info-card">
                  <span className="sa-profit-info-label">Total Stakers</span>
                  <span className="sa-profit-info-value">
                    {profitConfig.total_stakers}
                  </span>
                </div>
              </div>

              {/* Distribute Button + Pause Toggle */}
              <div className="sa-profit-distribute-row">
                <button
                  className="sa-profit-distribute-btn"
                  onClick={handleDistribute}
                  disabled={distributing || profitConfig.paused}
                >
                  {distributing ? "Distributing..." : "Distribute Profit"}
                </button>
                <button
                  className={`sa-profit-pause-btn ${profitConfig.paused ? "paused" : "active"}`}
                  onClick={handlePauseToggle}
                  disabled={togglingPause}
                >
                  {togglingPause
                    ? "Updating..."
                    : profitConfig.paused
                      ? "▶ Unpause Contract"
                      : "⏸ Pause Contract"}
                </button>
                {profitConfig.paused && (
                  <span className="sa-profit-paused-badge">
                    Contract Paused
                  </span>
                )}
              </div>

              {/* Stakers + Profit Balance List */}
              <div className="sa-profit-stakers">
                <h4>Stakers &amp; Profit Balances</h4>
                {profitStakers.length === 0 ? (
                  <div className="no-markets">No stakers found.</div>
                ) : (
                  <>
                    <div className="sa-profit-stakers-header">
                      <span>Account</span>
                      <span>Staked</span>
                      <span>Positions</span>
                      <span>Profit Balance</span>
                    </div>
                    <div className="sa-profit-stakers-list">
                      {profitStakers.map((staker) => {
                        const stakeSymbol = parseProfitSymbol(
                          profitConfig.stake_token_symbol,
                        );
                        const profitSymbol = parseProfitSymbol(
                          profitConfig.profit_token_symbol,
                        );
                        const balRow = profitBalances.find(
                          (b) => b.user === staker.account,
                        );
                        let balDisplay = `0.${"0".repeat(profitSymbol.precision)} ${profitSymbol.name}`;
                        if (balRow) {
                          // balance is an asset string like "12.345678 XUSDC" or raw object
                          if (typeof balRow.balance === "string") {
                            balDisplay = balRow.balance;
                          } else if (typeof balRow.balance === "number") {
                            balDisplay = `${formatProfitAmount(balRow.balance, profitSymbol.precision)} ${profitSymbol.name}`;
                          }
                        }
                        return (
                          <div
                            className="sa-profit-staker-row"
                            key={staker.account}
                          >
                            <span className="sa-profit-staker-account">
                              {staker.account}
                            </span>
                            <span className="sa-profit-staker-staked">
                              {formatProfitAmount(
                                staker.total_staked,
                                stakeSymbol.precision,
                              )}{" "}
                              {stakeSymbol.name}
                            </span>
                            <span className="sa-profit-staker-count">
                              {staker.stake_count}
                            </span>
                            <span className="sa-profit-staker-balance">
                              {balDisplay}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "marketapproval" && (
        <MarketApprovalSection
          pendingMarkets={pendingMarkets}
          loadingPending={loadingPending}
          liquidityInputs={liquidityInputs}
          setLiquidityInputs={setLiquidityInputs}
          declineInputs={declineInputs}
          setDeclineInputs={setDeclineInputs}
          approvingId={approvingId}
          decliningId={decliningId}
          platformPoolBalance={platformPoolBalance}
          scale={SCALE}
          computeUsdtCost={computeUsdtCost}
          onSetLiquidity={handleSetLiquidity}
          onDeclineMarket={handleDeclineMarket}
        />
      )}

      {activeTab === "illiquid" && (
        <div className="admin-section">
          <h3>Illiquid Markets</h3>
          <p className="section-description" style={{ color: "#f59e0b" }}>
            Expired markets with no trading or one-sided trading. Mark them as
            refundable so traders can claim back their invested amounts.
          </p>

          {loadingIlliquid ? (
            <Loader
              label="Loading illiquid markets..."
              className="app-loader-compact"
            />
          ) : illiquidMarkets.length === 0 ? (
            <div className="no-markets">No illiquid markets found.</div>
          ) : (
            <div className="market-cards">
              {illiquidMarkets.map((market) => (
                <div
                  key={market.id}
                  className="market-card"
                  style={{
                    border: market.is_refundable
                      ? "1px solid #10b981"
                      : "1px solid #374151",
                    backgroundColor: market.is_refundable
                      ? "rgba(16, 185, 129, 0.05)"
                      : "transparent",
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    className="market-question"
                    style={{ fontWeight: "bold", marginBottom: "8px" }}
                  >
                    #{market.id}: {market.question}
                  </div>
                  <div
                    className="market-meta"
                    style={{
                      fontSize: "0.85em",
                      color: "#9ca3af",
                      marginBottom: "8px",
                    }}
                  >
                    Category: {market.category} | Proposed by:{" "}
                    {market.suggested_by} | Type:{" "}
                    {market.outcomes_count === 2
                      ? "Binary"
                      : `${market.outcomes_count}-outcome`}
                  </div>
                  <div
                    className="market-meta"
                    style={{
                      fontSize: "0.85em",
                      color: "#9ca3af",
                      marginBottom: "8px",
                    }}
                  >
                    Expired: {new Date(market.expire * 1000).toLocaleString()}
                    {" | Volume: "}
                    {Math.floor(market.total_collateral_in / 1_000_000)} {TRADING_TOKEN}
                  </div>

                  {/* Outcomes */}
                  {market.outcomes && market.outcomes.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "wrap",
                        marginBottom: "10px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.85em",
                          color: "#9ca3af",
                          fontWeight: 600,
                        }}
                      >
                        Outcomes:
                      </span>
                      {market.outcomes.map((o, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: "2px 10px",
                            borderRadius: "12px",
                            fontSize: "0.8em",
                            backgroundColor: "rgba(139, 92, 246, 0.15)",
                            color: "#a78bfa",
                          }}
                        >
                          {o.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Status badges */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginBottom: "12px",
                    }}
                  >
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "0.8em",
                        backgroundColor:
                          market.illiquidType === "no_trading"
                            ? "rgba(239, 68, 68, 0.2)"
                            : "rgba(245, 158, 11, 0.2)",
                        color:
                          market.illiquidType === "no_trading"
                            ? "#ef4444"
                            : "#f59e0b",
                        fontWeight: 600,
                      }}
                    >
                      {market.illiquidType === "no_trading"
                        ? "No Trading"
                        : "One-Sided Trading"}
                    </span>
                    {market.is_refundable && (
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontSize: "0.8em",
                          backgroundColor: "rgba(16, 185, 129, 0.2)",
                          color: "#10b981",
                          fontWeight: 600,
                        }}
                      >
                        Refundable
                      </span>
                    )}
                  </div>

                  {/* Action */}
                  {!market.is_refundable ? (
                    <button
                      onClick={() => handleMarkRefundable(market.id)}
                      disabled={markingRefundableId === market.id}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontWeight: "bold",
                        fontSize: "0.9em",
                        backgroundColor:
                          markingRefundableId === market.id
                            ? "#374151"
                            : "#f59e0b",
                        color:
                          markingRefundableId === market.id
                            ? "#6b7280"
                            : "#000",
                        border: "none",
                        cursor:
                          markingRefundableId === market.id
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {markingRefundableId === market.id
                        ? "Marking..."
                        : "Mark as Refundable"}
                    </button>
                  ) : (
                    <div
                      style={{
                        fontSize: "0.85em",
                        color: "#10b981",
                        fontWeight: 600,
                      }}
                    >
                      This market has been marked as refundable. Traders can
                      claim back their invested amounts.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "editany" && (
        <SuperAdminEditMarketSection session={session} showToast={showToast} />
      )}
    </div>
  );
};

export default SuperAdminPanel;
